import React from 'react';
import { FormattedMessage } from 'react-intl';
import filter from 'lodash/filter';
import find from 'lodash/find';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import isString from 'lodash/isString';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import sortBy from 'lodash/sortBy';

import { scriptStepTypes } from '@xmatters/common/constants/scriptSteps';
import { splitEscaped } from '@xmatters/common/utils/string';

import {
  CreateAlertInputDefaultsByName,
  FlowInputId,
  FormPropertyInputDefault,
  FormPropertyType,
  FormSectionType,
} from 'xm-constants/flowInput';
import { DeviceFilterType } from 'xm-constants/device';
import { incidentTypeInputName } from 'xm-constants/initiateIncidentStep';
import { isInitiateIncidentBlock } from 'utils/flow/initiateIncident';
import { bridgeTypes } from '../pages/FlowBlockInput/CreateXmEventModal/CreateXmEventConferenceBridge/constants';

/*
 * Serializers and Deserializers to help hyrax build the data fr xm-api:
 */
// Default serializers/deserializers (what most inputs use):
export const defaultSerializeForAPI = value => value;
export const defaultDeserializeForUI = str => str;

export const isVarInputName = name => name.startsWith('variable.');
export const stripVariablePrefix = name => name.replace(/^variable\./, '');

export const getSelectedDevicesCommaSeparated = devices =>
  devices
    .filter(device => device.selected)
    .map(device => device.name)
    .join(',');

const isListPropertyType = propertyType =>
  propertyType === 'LIST' || propertyType === 'LIST_TEXT_MULTI_SELECT';

// Specialized serializers/deserializers for different form property types:
function deserializeList(value) {
  let response = '';
  try {
    const json = JSON.parse(value);
    response = json.join(',');
  } catch (e) {
    // Do nothing.
  }
  return defaultDeserializeForUI(response);
}
function serializeList(value) {
  if (!value) {
    return null;
  }

  return JSON.stringify(defaultSerializeForAPI(value).split(','));
}

export function deserializeIdList(value) {
  let response = '';
  try {
    const json = JSON.parse(value || '');
    response = json.map(i => i.id).join(',');
  } catch (e) {
    // Do nothing.
  }
  return defaultDeserializeForUI(response);
}
export function serializeIdList(value, escapeBackslash) {
  if (!value) {
    return null;
  }
  let val = value;
  if (escapeBackslash) {
    val = val.replace(/\\/g, '\\\\');
  }
  const ids = defaultSerializeForAPI(val).split(',');
  const serialized = map(ids, id => `{"id":"${id}"}`);
  return `[${serialized.join(',')}]`;
}

const HIERARCHY_SPLITTER = '>';
function deserializeHierarchy(initialValue) {
  let response = '';
  try {
    const value = (initialValue || '').replace(/\\/g, '\\\\');
    const json = value ? JSON.parse(value) : value;
    response = json.map(i => i.value || '').join(HIERARCHY_SPLITTER);
  } catch (e) {
    // Do nothing.
  }
  return defaultDeserializeForUI(response);
}
function serializeHierarchy(userInputValue) {
  if (!userInputValue) {
    return null;
  }

  const values = splitEscaped(
    defaultSerializeForAPI(userInputValue),
    HIERARCHY_SPLITTER,
  );
  // Return null if the field is empty (Can be [] or [""])
  if (values.length === 0 || values.every(val => !val)) {
    return null;
  }

  const serialized = map(values, val => `{"value":"${val}"}`);
  return `[${serialized.join(',')}]`;
}

export const getDeserializer = scriptStepInput => {
  const { propertyType } = scriptStepInput;

  if (propertyType === 'RECIPIENT') {
    return deserializeIdList;
  }
  if (isListPropertyType(propertyType)) {
    return deserializeList;
  }
  if (propertyType === 'HIERARCHY') {
    return deserializeHierarchy;
  }

  return defaultDeserializeForUI;
};

export const getSerializer = scriptStepInput => {
  const { propertyType, formProperty } = scriptStepInput;

  if (propertyType === 'RECIPIENT') {
    return serializeIdList;
  }
  if (isListPropertyType(propertyType)) {
    return serializeList;
  }
  if (propertyType === 'HIERARCHY') {
    return value => serializeHierarchy(value, formProperty);
  }

  return defaultSerializeForAPI;
};

/*
 * Input builders:
 */

const getDataProcessors = input => ({
  serializer: getSerializer(input),
  deserializer: getDeserializer(input),
});

const getTooltip = propertyType => {
  const suffix = propertyType.toUpperCase();
  const messageId = `FLOW_DESIGN_CREATE_EVENT_INPUT_FORM_PROP_TOOLTIP_${suffix}`;

  return <FormattedMessage id={messageId} />;
};

function makeGenericPropertyInput(property, position) {
  const {
    id,
    sectionId,
    defaultValue,
    description,
    helpText,
    required,
    minLength,
    maxLength,
    name,
    propertyType,
    multilineText,
    hasTooltip = true,
  } = property;

  return {
    id,
    scriptStep: {
      id: sectionId,
    },
    name,
    // Form properties use `defaultValue`, Incident Type properties use `default`:
    defaultValue: defaultValue ?? property.default,
    minLength,
    maxLength,
    required: !!required,
    multiLine: !!multilineText,
    position,
    propertyType,
    // Previous code was showing description element based on the actual stored "helpText"...
    // I think for COR-11751 the goal was to show form property descriptions there,
    // and for the _real_ "helpText" property (as returned by the API) to appear in a tooltip.
    helpText: description,
    description: helpText,
    tooltip: hasTooltip ? getTooltip(propertyType) : null,

    // TODO: these aren't generic, but backend may keep using the same naming... Consider if we can get more generic names going at some point.
    formProperty: property,
    isFormSectionItem: true,
  };
}

const makeFormPropertyInput = (formProperty, i) => {
  const input = makeGenericPropertyInput(formProperty, i + 1);
  const { serializer, deserializer } = getDataProcessors(input);
  return {
    ...input,
    serializer,
    deserializer,
  };
};

const makeFlowBlockInput = (blockId, scriptStepInput) => ({
  flowBlock: { id: blockId },
  value: scriptStepInput.defaultValue,
  scriptStepInput,
});

const getInputUniqKeySimple = (blockId, inputId) => `${blockId}_${inputId}`;
const getInputUniqKey = input => {
  const { flowBlock, formSectionItem, scriptStepInput } = input;
  const inputId = formSectionItem?.id ?? scriptStepInput?.id;
  return getInputUniqKeySimple(flowBlock?.id, inputId);
};

const getRecipientsPanelDefaultInputs = ({ recipients }) => {
  const recipientData = recipients && recipients.data;
  return [
    {
      id: FlowInputId.RECIPIENTS,
      required: false,
      property: {
        id: FlowInputId.RECIPIENTS,
        propertyType: FormPropertyType.RECIPIENTS,
        name: 'Recipients',
        label: 'Recipients',
        description: (
          <FormattedMessage id="FLOW_DESIGN_CREATE_EVENT_INPUT_HELP_TEXT_RECIPIENTS" />
        ),
        // xm-api provides the default value for RECIPIENTS panels under `recipients`. It's an Array of Objects that
        // have all the standard recipient model data available, but we likely only care about id or targetName
        defaultValue: map(recipientData, 'targetName').join(','),
        hasTooltip: false,
      },
    },
  ];
};

const createFormPanelDefaultInput = ({
  id,
  formDefaultValue,
  sectionId,
  name,
  ...otherProps
}) => ({
  id,
  required: false,
  configurable: false,
  property: {
    id,
    sectionId,
    name: name || id,
    label: name,
    defaultValue: '', // no override
    notAutoConfigurable: true, // don't show this together with the rest of the inputs in Setup tab
    formDefaultValue: formDefaultValue || FormPropertyInputDefault[id],
    ...otherProps,
  },
});

const createHandlingOptionsPanelDefaultInput = props =>
  createFormPanelDefaultInput({
    sectionId: FormSectionType.HANDLING_OPTIONS,
    propertyType: FormPropertyType.HANDLING_OPTIONS,
    ...props,
  });

const createSenderOverridesPanelDefaultInput = props =>
  createFormPanelDefaultInput({
    sectionId: FormSectionType.SENDER_OVERRIDES,
    propertyType: FormPropertyType.SENDER_OVERRIDES,
    ...props,
  });

const createDeviceFilterPanelDefaultInput = props =>
  createFormPanelDefaultInput({
    sectionId: FormSectionType.DEVICE_FILTER,
    propertyType: FormPropertyType.DEVICE_FILTER,
    ...props,
  });

const createConferenceBridgeDefaultInput = props =>
  createFormPanelDefaultInput({
    sectionId: FormSectionType.CONFERENCE_BRIDGE,
    propertyType: FormPropertyType.CONFERENCE_BRIDGE,
    ...props,
  });

const getDefaultPanelPropertyInputs = panel => {
  switch (panel.type) {
    case FormSectionType.RECIPIENTS:
      return getRecipientsPanelDefaultInputs(panel);
    default:
      return null;
  }
};

const getDefaultFormPropertyInputs = (
  planLayout,
  isIncidentManagementEnabled,
) => {
  const deviceFilterPanel = find(planLayout, {
    type: FormSectionType.DEVICE_FILTER,
  });
  const targetDeviceNames = get(
    deviceFilterPanel,
    'targetDeviceNames.data',
    [],
  );

  const handlingOptionsPanel =
    find(planLayout, {
      type: FormSectionType.HANDLING_OPTIONS,
    }) || {};
  const {
    priority,
    expirationInMinutes,
    overrideDeviceRestrictions,
    escalationOverride,
    bypassPhoneIntro,
    requirePhonePassword,
    voicemailOptions,
  } = handlingOptionsPanel;

  const senderOverridesPanel =
    find(planLayout, {
      type: FormSectionType.SENDER_OVERRIDES,
    }) || {};
  const { displayName, callerId } = senderOverridesPanel;

  const conferenceBridgePanel =
    find(planLayout, {
      type: FormSectionType.CONFERENCE_BRIDGE,
    }) || {};
  const { bridgeType } = conferenceBridgePanel;

  return [
    createConferenceBridgeDefaultInput({
      id: FlowInputId.CONFERENCE_BRIDGE,
      name: FlowInputId.CONFERENCE_BRIDGE,
      formDefaultValue: JSON.stringify({
        bridgeTypeSelector:
          bridgeType === bridgeTypes.EXTERNAL_BRIDGES_ONLY
            ? bridgeTypes.EXTERNAL_BRIDGES_ONLY
            : bridgeTypes.XMATTERS_BRIDGES_ONLY,
      }),
    }),
    createDeviceFilterPanelDefaultInput({
      id: deviceFilterPanel?.targetDeviceNamesId,
      name: FlowInputId.DEVICE_FILTER,
      formDefaultValue:
        targetDeviceNames &&
        getSelectedDevicesCommaSeparated(targetDeviceNames),
    }),
    createDeviceFilterPanelDefaultInput({
      id: FlowInputId.DEVICE_FILTER_UI_TYPE,
      name: FlowInputId.DEVICE_FILTER_UI_TYPE,
      formDefaultValue: DeviceFilterType.PICKER,
    }),
    createHandlingOptionsPanelDefaultInput({
      id: priority && priority.id,
      name: FlowInputId.PRIORITY,
      formDefaultValue: priority && priority.value,
    }),
    createHandlingOptionsPanelDefaultInput({
      id: expirationInMinutes && expirationInMinutes.id,
      name: FlowInputId.EXPIRATION,
      formDefaultValue: expirationInMinutes && expirationInMinutes.value,
    }),
    createHandlingOptionsPanelDefaultInput({
      id: overrideDeviceRestrictions && overrideDeviceRestrictions.id,
      name: FlowInputId.OVERRIDE_DEVICE_RESTRICTIONS,
      formDefaultValue:
        overrideDeviceRestrictions && `${overrideDeviceRestrictions.value}`,
    }),
    createHandlingOptionsPanelDefaultInput({
      id: escalationOverride && escalationOverride.id,
      name: FlowInputId.ESCALATION_OVERRIDE,
      formDefaultValue: escalationOverride && `${escalationOverride.value}`,
    }),
    createHandlingOptionsPanelDefaultInput({
      id: bypassPhoneIntro && bypassPhoneIntro.id,
      name: FlowInputId.BYPASS_PHONE_INTRO,
      formDefaultValue: bypassPhoneIntro && `${bypassPhoneIntro.value}`,
    }),
    createHandlingOptionsPanelDefaultInput({
      id: requirePhonePassword && requirePhonePassword.id,
      name: FlowInputId.REQUIRE_PHONE_PASSWORD,
      formDefaultValue: requirePhonePassword && `${requirePhonePassword.value}`,
    }),
    createHandlingOptionsPanelDefaultInput({
      id: voicemailOptions && voicemailOptions.retryId,
      name: FlowInputId.VOICEMAIL_OPTIONS_RETRY,
      formDefaultValue: voicemailOptions && `${voicemailOptions.retry}`,
    }),
    createHandlingOptionsPanelDefaultInput({
      id: voicemailOptions && voicemailOptions.everyId,
      name: FlowInputId.VOICEMAIL_OPTIONS_EVERY,
      formDefaultValue: voicemailOptions && `${voicemailOptions.every}`,
    }),
    createHandlingOptionsPanelDefaultInput({
      id: voicemailOptions && voicemailOptions.leaveId,
      name: FlowInputId.VOICEMAIL_OPTIONS_LEAVE,
      formDefaultValue: voicemailOptions && `${voicemailOptions.leave}`,
    }),
    createSenderOverridesPanelDefaultInput({
      id: displayName && displayName.id,
      name: FlowInputId.SENDER_DISPLAY_NAME,
      formDefaultValue: displayName && `${displayName.value}`,
    }),
    createSenderOverridesPanelDefaultInput({
      id: callerId && callerId.id,
      name: FlowInputId.SENDER_CALLER_ID,
      formDefaultValue: callerId && `${callerId.value}`,
    }),
    ...(isIncidentManagementEnabled
      ? [
          {
            id: FlowInputId.INCIDENT,
            required: false,
            property: {
              id: FlowInputId.INCIDENT,
              sectionId: FormSectionType.INCIDENT,
              propertyType: FormPropertyType.INCIDENT_ID,
              name: 'Incident ID',
              label: 'Incident ID ',
              defaultValue: '',
              description: (
                <FormattedMessage id="FLOW_DESIGN_CREATE_ALERT_INPUT_HELP_TEXT_INCIDENT_ID" />
              ),
            },
          },
        ]
      : []),
  ].filter(item => item?.id);
};

function upsertSyncedInputs({ inputs, updatedInputsByKey }) {
  const seenKeys = new Set();
  // Replace existing inputs:
  const results = inputs.map(input => {
    const key = getInputUniqKey(input);
    const updatedInput = updatedInputsByKey.get(key);
    if (updatedInput) {
      seenKeys.add(key);
      return updatedInput;
    }
    return input;
  });
  // Add remaining (newly created) inputs:
  updatedInputsByKey.forEach((input, key) => {
    if (!seenKeys.has(key)) {
      results.push(input);
    }
  });
  return results;
}

function setSyncedInputsForBlocks({
  affectedBlocks,
  stepInputData,
  inputsByKeyObj,
  syncedInputsByKeyMap,
}) {
  forEach(affectedBlocks, block => {
    const key = getInputUniqKeySimple(block.id, stepInputData.id);
    const existingBlockInput = inputsByKeyObj[key];
    const newInput = existingBlockInput
      ? { ...existingBlockInput, scriptStepInput: stepInputData }
      : makeFlowBlockInput(block.id, stepInputData);
    syncedInputsByKeyMap.set(key, newInput);
  });
}

/**
 * removeInvalidPreviouslySyncedInputs handles removing inputs that were previously synced but not longer relevant.
 *   This includes items made for deleted properties, or items made for an entity no longer associated with a block
 *   (e.g. changing the linked form on Create Event, changing the Incident Type on Initiate Incident, etc.)
 */
function removeInvalidPreviouslySyncedInputs({
  inputs,
  isRelevantInput,
  updatedInputsByKey,
}) {
  return filter(inputs, input => {
    if (!isRelevantInput(input)) {
      return true; // Leave all the unrelated inputs alone!
    }
    // Keep only the remaining items that were resynced, via their presence in updatedInputsByKey:
    return updatedInputsByKey.has(getInputUniqKey(input));
  });
}

/**
 * generateInputsByKeyMapFromFormPropertyData creates a Map object where the keys are based on getInputUniqKeySimple
 * and the values represent an input.
 *
 * The results of this function are expected be used for diffing, so used for the purposes of:
 * - Update existing inputs with new data (e.g. if a user changed a Property's name, update the input's name too)
 * - Add new inputs that didn't exist (e.g. if a user added new Properties to existing
 *     Workflow Forms, create inputs for those for the relevant blocks)
 * - Remove stale data (e.g. if the user deleted a Property from a Workflow Form then it may have an input
 *     under `state.inputs` but it won't have an item in the generated updatedInputsByKey)
 */
function generateInputsByKeyMapFromFormPropertyData({
  createEventBlocksById,
  inputs,
  isIncidentManagementEnabled,
  planLayoutByFormId,
}) {
  const updatedInputsByKey = new Map();
  const createEventInputs = filter(
    inputs,
    i => createEventBlocksById[i.flowBlock && i.flowBlock.id],
  );
  const createEventInputsByKey = keyBy(createEventInputs, getInputUniqKey);
  const createEventBlocksByFormId = groupBy(
    createEventBlocksById,
    b => b.step.id,
  );

  let i = 0;
  const handlePanelPropertyInput = (formId, panel, inputItem) => {
    i += 1;
    const { property, orderNum, required, multilineText } = inputItem;
    const { id: sectionId } = panel;
    setSyncedInputsForBlocks({
      affectedBlocks: createEventBlocksByFormId[formId],
      stepInputData: makeFormPropertyInput(
        {
          ...property,
          orderNum,
          required,
          multilineText,
          sectionId,
        },
        i,
      ),
      inputsByKeyObj: createEventInputsByKey,
      syncedInputsByKeyMap: updatedInputsByKey,
    });
  };

  const getFilteredItems = items =>
    sortBy(filter(items, 'visible'), 'orderNum');

  forEach(planLayoutByFormId, (planLayout, formId) => {
    getFilteredItems(planLayout).forEach(panel => {
      const { items } = panel;

      getFilteredItems(items).forEach(item => {
        // If it doesn't have property defined, and isn't one we expect, don't show the input (since we're likely to get it wrong):
        if (item.propertyDefinition) {
          const { default: defaultValue } = item.propertyDefinition;
          // For ones properly under _property_ need to namespace the name to avoid collisions (e.g. if a user named a property `recipients`):
          const property = {
            ...item.propertyDefinition,
            defaultValue:
              defaultValue !== undefined && !isString(defaultValue)
                ? JSON.stringify(defaultValue)
                : defaultValue,
          };
          handlePanelPropertyInput(formId, panel, { ...item, property });
        }
      });

      if (!items) {
        const defaultInputs = getDefaultPanelPropertyInputs(panel);
        if (defaultInputs) {
          defaultInputs.forEach(input =>
            handlePanelPropertyInput(formId, panel, input),
          );
        }
      }
    });

    const defaultInputs = getDefaultFormPropertyInputs(
      planLayout,
      isIncidentManagementEnabled,
    );
    defaultInputs.forEach(input =>
      handlePanelPropertyInput(formId, { id: input.property.sectionId }, input),
    );
  });
  return updatedInputsByKey;
}

export const syncFormPropertyStepInputData = ({
  inputs,
  blocks,
  planLayoutByFormId,
  isIncidentManagementEnabled,
}) => {
  const createEventBlocks = filter(
    blocks,
    b => b.step.type === scriptStepTypes.CREATE_EVENT,
  );
  if (!createEventBlocks.length || !planLayoutByFormId) {
    return inputs; // Nothing do do, don't bother doing all the work
  }
  const createEventBlocksById = keyBy(createEventBlocks, 'id');

  // First we generate inputs for the data, storing these in a new Map
  const updatedInputsByKey = generateInputsByKeyMapFromFormPropertyData({
    createEventBlocksById,
    inputs,
    isIncidentManagementEnabled,
    planLayoutByFormId,
  });

  // Using our Map of inputs we add or update items as needed (creates new Array and Objects as needed, no mutations):
  const updatedInputs = upsertSyncedInputs({ inputs, updatedInputsByKey });

  // Finally, we remove inputs that previously existed but no longer match the form data (e.g. removed properties):
  return removeInvalidPreviouslySyncedInputs({
    inputs: updatedInputs,
    updatedInputsByKey,
    isRelevantInput: input => {
      const block = createEventBlocksById[input.flowBlock?.id];
      // Input is from a dynamic property:
      return (
        input.formSectionItem?.id &&
        // AND input is part of a block:
        block &&
        // AND layout is loaded, and this block is configured to point to a loaded form, otherwise leave input until we've loaded more data
        planLayoutByFormId[block.step.id]
      );
    },
  });
};

/**
 * generateInputsByKeyMapFromIncidentTypesData creates a Map object where the keys are based on getInputUniqKeySimple
 * and the values represent an input.
 *
 * The results of this function are expected be used for diffing, so used for the purposes of:
 * - Update existing inputs with new data (e.g. if a user changed a Property's name, update the input's name too)
 * - Add new inputs that didn't exist (e.g. if a user added new Properties to existing
 *     Incident Types, create inputs for those for the relevant blocks)
 * - Remove stale data (e.g. if the user deleted a Property from an Incident Type then it may have an input
 *     under `state.inputs` but it won't have an item in the generated updatedInputsByKey)
 */
function generateInputsByKeyMapFromIncidentTypesData({
  incidentTypes,
  inputs,
  initiateIncidentBlocksById,
}) {
  const updatedInputsByKey = new Map();
  const initiateIncidentInputs = filter(
    inputs,
    i => initiateIncidentBlocksById[i.flowBlock?.id],
  );
  const initiateIncidentInputsByKey = keyBy(
    initiateIncidentInputs,
    getInputUniqKey,
  );

  // NOTE: these "BlockRefs" aren't whole blocks, they just have an id, like `{ id: $WHATEVER }`
  const initiateIncidentBlockRefsByTypeId = inputs
    .filter(
      input =>
        input?.scriptStepInput?.name === incidentTypeInputName && input.value,
    )
    .reduce((acc, input) => {
      acc[input.value] = acc[input.value] ?? [];
      acc[input.value].push(input.flowBlock);
      return acc;
    }, {});

  forEach(incidentTypes, incidentType => {
    forEach(sortBy(incidentType.properties, 'orderNum'), (property, i) => {
      setSyncedInputsForBlocks({
        // NOTE: these "affectedBlocks" aren't whole blocks, they just have an id, like `{ id: $WHATEVER }`
        affectedBlocks: initiateIncidentBlockRefsByTypeId[incidentType.id],
        stepInputData: makeGenericPropertyInput(property, i + 1),
        inputsByKeyObj: initiateIncidentInputsByKey,
        syncedInputsByKeyMap: updatedInputsByKey,
      });
    });
  });
  return updatedInputsByKey;
}

export function syncIncidentTypePropertyStepInputData({
  inputs,
  blocks,
  incidentTypes,
  customSteps,
}) {
  const initiateIncidentBlocks = filter(blocks, b =>
    isInitiateIncidentBlock(b, customSteps),
  );
  if (!initiateIncidentBlocks.length || !incidentTypes?.length) {
    return inputs; // Nothing do do, don't bother doing all the work
  }
  const initiateIncidentBlocksById = keyBy(initiateIncidentBlocks, 'id');

  // First we generate inputs for the data, storing these in a new Map
  const updatedInputsByKey = generateInputsByKeyMapFromIncidentTypesData({
    incidentTypes,
    inputs,
    initiateIncidentBlocksById,
  });

  // Using our Map of inputs we add or update items as needed (creates new Array and Objects as needed, no mutations):
  const updatedInputs = upsertSyncedInputs({ inputs, updatedInputsByKey });

  // Finally, we remove inputs that previously existed but no longer match the form data (e.g. removed properties):
  return removeInvalidPreviouslySyncedInputs({
    inputs: updatedInputs,
    updatedInputsByKey,
    isRelevantInput: input =>
      // Input is from a dynamic property:
      input.formSectionItem?.id &&
      // AND Input is part of an Initiate Incident block:
      initiateIncidentBlocksById[input.flowBlock.id],
  });
}

export function deserializeCreateAlertTriggers(value, defaultValue = {}) {
  if (value && value !== '') {
    try {
      return JSON.parse(value);
    } catch (e) {
      // Do nothing.
    }
  }
  return defaultValue;
}

export function serializeCreateAlertTriggers(value) {
  return JSON.stringify(value);
}

// Currently these methods are identical to the `triggers` equivalents.
// Separate methods for if they diverge in the future, and also for code readability on the consumer side.
export function deserializeCreateAlertResponseOptions(value) {
  return deserializeCreateAlertTriggers(value, []);
}
export function serializeCreateAlertResponseOptions(value) {
  return JSON.stringify(value);
}

const getCreateAlertInputDefaultValue = name =>
  CreateAlertInputDefaultsByName[name] ?? '';

export function syncCreateAlertInputData({ blocks, builtInInputs, inputs }) {
  const createAlertInputs = builtInInputs?.filter(
    input => input.builtInType === scriptStepTypes.CREATE_ALERT,
  );
  const existingInputsByBlockId = groupBy(inputs, 'flowBlock.id');
  const newInputs = [];

  forEach(blocks, block => {
    if (block?.step?.type === scriptStepTypes.CREATE_ALERT) {
      const blockInputs = existingInputsByBlockId[block.id] || [];
      const blockInputsByName = keyBy(blockInputs, 'scriptStepInput.name');

      forEach(createAlertInputs, alertInput => {
        // Prevent duplicates (e.g. we only stores rows in DB if they have a non-blank value, so add in the others):
        if (!blockInputsByName[alertInput.name]) {
          newInputs.push({
            flowBlock: { id: block.id },
            scriptStepInput: { ...alertInput, isBuiltInInput: true },
            value: getCreateAlertInputDefaultValue(alertInput.name),
          });
        }
      });
    }
  });

  return [...inputs, ...newInputs];
}

/**
 * Global regexes are stateful, store a `lastIndex` that changes as it's used.
 * Meaning some methods such as `test` can return inconsistent results
 *   ^ example: https://stackoverflow.com/questions/1520800/why-does-a-regexp-with-global-flag-give-wrong-results
 * As such it is safer to use a methods to get a fresh regex object each time it's used,
 * or otherwise be very cautious if using a global copy.
 */
export const getVarRegex = () => /\${([^{}]*)}/g;
